import React, { useState, useRef, Fragment, useEffect, useCallback } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon, TrashIcon, PlusIcon, EyeIcon, EyeSlashIcon, ArrowRightIcon, ChevronDownIcon, AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { Input, Select, Textarea } from "components/ui/Form";
import { getMasterFormFields } from "services/eventScoreService";
import clsx from "clsx";
import axios from "utils/axios";
import { FieldSelectionModal } from "../../../app/pages/settings/sections/builder/FieldSelectionModal";
import { SubmitFormForLead, getFormById, getFieldTypes } from "services/formBuilderService";
import { toast } from "sonner";
import { mutate } from "swr";
import formValidationSchema from "../LeadActions/QuickLeadValidation";
import { updateLeadDetail } from "../../../services/leadsService";
import { contactService } from "services/contactService";
import { submitApplicationAfterStageChange } from "services/applicantService";
import { getCompanyIdFromToken, getUserIdFromToken } from "../../../utils/tokenUtils";
import { getStageAndSubStages } from "services/leadStageService";
import { getUsersForAssignLeads } from "services/userService";
import PhoneInput, { getCountryCallingCode } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import enLocale from 'react-phone-number-input/locale/en.json';
import PaymentDetailsModal from "./PaymentDetailsModal";

// Create a reverse mapping of country names to codes
const countryNameToCode = Object.entries(enLocale).reduce((acc, [code, name]) => {
    acc[name.toLowerCase()] = code;
    return acc;
}, {});

// Helper to normalize phone numbers with + and country calling code
const getNormalizedPhoneNumber = (phoneVal, countryName = 'India') => {
    if (!phoneVal || typeof phoneVal !== 'string') {
        return phoneVal;
    }
    // Remove all non-numeric characters except +
    const cleaned = phoneVal.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+')) {
        return cleaned;
    }
    const countryCode = countryNameToCode[String(countryName).toLowerCase()] || 'IN';
    try {
        const callingCode = getCountryCallingCode(countryCode);
        if (cleaned.startsWith(callingCode)) {
            return `+${cleaned}`;
        } else {
            return `+${callingCode}${cleaned}`;
        }
    } catch (e) {
        console.warn("Could not get calling code for country:", countryName);
        return cleaned;
    }
};

// Field name mapping helper
const getSchemaKey = (fieldName) => {
    const lowerKey = fieldName.toLowerCase();
    if (lowerKey.includes('firstname') || lowerKey.includes('first_name') || lowerKey === 'firstname') {
        return 'firstName';
    } else if (lowerKey === 'email') {
        return 'email';
    } else if (lowerKey.includes('phone') || lowerKey.includes('mobile')) {
        return 'phone';
    } else if (lowerKey === 'country') {
        return 'country';
    } else if (lowerKey === 'semester') {
        return 'semester';
    }
    return null;
};


const FieldContainer = ({
    children,
    isCheckbox = false,
    fieldId,
    field,
    isHovered,
    isDisabled: isFieldDisabled,
    onMouseEnter,
    onMouseLeave,
    onDelete,
    fieldRefs,
    error,
    hideDescription = false
}) => (
    <div
        key={fieldId}
        ref={(el) => el && (fieldRefs[field.FieldName] = el)}
        className={clsx(
            "relative transition-all duration-300 ease-out p-3 rounded-xl border",
            error
                ? "bg-red-50/40 dark:bg-red-900/20 border-red-300 dark:border-red-600"
                : isHovered
                    ? "bg-primary-50/40 dark:bg-primary-900/20 border-primary-300 dark:border-primary-600 transform scale-[1.01] shadow-md"
                    : "bg-white dark:bg-dark-700 border-gray-200 dark:border-dark-600 shadow-sm",
            "hover:shadow-md hover:border-primary-200 dark:hover:border-primary-600"
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
    >
        {/* Floating Tooltip for Field Category (Mandatory / Custom) */}
        {isHovered && (
            <div className="absolute -top-6 left-2 z-30 pointer-events-none">
                <div
                    className={clsx(
                        "px-2 py-1 rounded text-xs font-medium shadow-lg",
                        field.IsMendatory
                            ? "bg-primary-600 text-white"
                            : "bg-primary-600 text-white"
                    )}
                >
                    {field.IsMendatory ? "Mandatory Field" : "Custom Field"}
                </div>
            </div>
        )}

        {/* Delete Button for Custom Fields */}
        {isHovered && field.isCustom && (
            <div className="absolute -top-2 -right-2 z-30">
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete(field.Id);
                    }}
                    className="w-8 h-8 bg-white hover:bg-red-50 text-red-600 hover:text-red-700 
         shadow-md hover:shadow-lg transition-all duration-200 rounded-lg 
         border border-red-200 flex items-center justify-center"
                    title="Delete field"
                >
                    <TrashIcon className="w-4 h-4 stroke-2" />
                </button>
            </div>
        )}
        <div className="relative z-10">
            {!isCheckbox && (
                <label
                    htmlFor={fieldId}
                    className={clsx(
                        "block text-sm font-medium mb-1 transition-colors duration-200",
                        error
                            ? "text-red-600 dark:text-red-400"
                            : isHovered
                                ? "text-primary-700 dark:text-primary-300"
                                : "text-gray-700 dark:text-gray-300"
                    )}
                >
                    {field.FieldLabel}
                    {field.IsMendatory ? (
                        <span className="text-red-500 ml-1">*</span>
                    ) : (
                        <span className="text-gray-400 text-xs ml-2 font-normal">(Optional)</span>
                    )}
                </label>
            )}
            {children}
            {error && (
                <p className="text-xs mt-1.5 text-red-600 dark:text-red-400 flex items-center gap-1 animate-fadeIn">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                </p>
            )}
            {!error && field.Description && !hideDescription && field.Description !== field.Placeholder && field.Description !== field.FieldLabel && (
                <p
                    className={clsx(
                        "text-xs mt-1 transition-colors duration-200",
                        isHovered
                            ? "text-primary-600 dark:text-primary-400"
                            : "text-gray-500 dark:text-gray-400"
                    )}
                >
                    {field.Description}
                </p>
            )}
        </div>
    </div>
);

const getFieldOptions = (field) => {
    if (!field) return [];
    let raw = field.MasterFormFieldOptions ?? field.options ?? field.Options ?? [];
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
        } catch (e) {
            if (raw.includes(',')) {
                return raw.split(',').map(s => s.trim()).filter(Boolean).map(val => ({
                    OptionLabel: val,
                    OptionValue: val
                }));
            }
            if (raw.trim()) {
                return [{ OptionLabel: raw.trim(), OptionValue: raw.trim() }];
            }
        }
        return [];
    }
    if (Array.isArray(raw)) {
        return raw;
    }
    return [];
};

const QuickLeadModal = ({ isOpen, onClose, leadId, categories, onLeadUpdated, selectedForm, isApplicationForm = false, isComingFromPayment = false }) => {
    const cancelButtonRef = useRef(null);
    const hasPatchedRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formFields, setFormFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const [formValues, setFormValues] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [userEditedFields, setUserEditedFields] = useState(new Set());
    const [hoveredFieldId, setHoveredFieldId] = useState(null);
    const [fieldRefs] = useState({});
    const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
    const [visibleFormFields, setVisibleFormFields] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [mandatoryFieldsByCategory, setMandatoryFieldsByCategory] = useState([]);
    const [disabledFields, setDisabledFields] = useState(new Set());
    const [currentStep, setCurrentStep] = useState(0);
    const [isMultiStep, setIsMultiStep] = useState(false);
    const [totalSteps, setTotalSteps] = useState(0);
    const [passwordVisible, setPasswordVisible] = useState({}); // Track visibility for password fields
    const [currentLeadId, setCurrentLeadId] = useState(leadId);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    
    // Assignment Configuration State
    const [stages, setStages] = useState([]);
    const [subStages, setSubStages] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedStage, setSelectedStage] = useState("");
    const [selectedSubStage, setSelectedSubStage] = useState("");
    const [assignmentType, setAssignmentType] = useState("rule"); // "rule" or "owner"
    const [selectedOwner, setSelectedOwner] = useState("");
    const [loadingAssignment, setLoadingAssignment] = useState(false);
    const [isAssignmentConfigOpen, setIsAssignmentConfigOpen] = useState(false);
    const ownerSelectRef = useRef(null);
    const assignmentSectionRef = useRef(null);

    useEffect(() => {
        setCurrentLeadId(leadId);
    }, [leadId]);

    // Master fields state for FieldSelectionModal (all fields from backend)
    const [allMasterFields, setAllMasterFields] = useState([]);
    const [masterFieldsLoading, setMasterFieldsLoading] = useState(false);
    const [masterFieldsError, setMasterFieldsError] = useState(null);
    const [fieldTypes, setFieldTypes] = useState([]); // Field types for CreateMasterFieldModal

    const isEditMode = !!(leadId && categories && categories.length > 0);

    // Add this useEffect to set disabled fields when in edit mode
    useEffect(() => {
        if (isEditMode && categories && categories.length > 0) {
            const fieldsToDisable = new Set();
            categories.forEach(cat => {
                const fields = cat.Fields || cat.MasterFields || cat.fields || [];
                fields.forEach(field => {
                    const fieldName = field.FieldName?.toLowerCase();
                    // Add email and phone fields to disabled set
                    if (fieldName === 'email' ||
                        fieldName === 'phone' ||
                        fieldName === 'phonenumber' ||
                        fieldName === 'mobile' ||
                        fieldName === 'contactnumber' ||
                        fieldName?.includes('phone') ||
                        fieldName?.includes('mobile')) {
                        fieldsToDisable.add(field.FieldName);
                    }
                });
            });
            setDisabledFields(fieldsToDisable);
        }
    }, [isEditMode, categories]);

    // Add this helper function to find a field in master fields
    const findFieldInMasterFields = (fieldName, masterFieldId) => {
        for (const category of formFields) {
            const field = category.MasterFields?.find(
                f => f.FieldName?.toLowerCase() === fieldName?.toLowerCase() || f.Id === masterFieldId
            );
            if (field) {
                return {
                    field: field,
                    category: category
                };
            }
        }
        return null;
    };
    // Updated patchIncomingValues function
    const patchIncomingValues = (categories) => {
        if (!categories || categories.length === 0 || formFields.length === 0) {
            return;
        }

        const patchedValues = {};
        const customFieldsToAdd = [];

        categories.forEach(cat => {
            cat.Fields?.forEach(f => {
                // Only process if FieldValue exists
                if (f.FieldName && f.FieldValue !== undefined && f.FieldValue !== null) {
                    // Check if this field is already in mandatory fields
                    const fieldInfo = findFieldInMasterFields(f.FieldName, f.MasterFieldId);

                    let processedValue = f.FieldValue;

                    // Normalize phone numbers if they are missing the international prefix
                    const isPhoneField = f.FieldName?.toLowerCase().includes('phone') || f.FieldName?.toLowerCase().includes('mobile');
                    if (isPhoneField && processedValue && typeof processedValue === 'string' && !processedValue.startsWith('+')) {
                        // Find country for this lead to get the calling code (searching all categories)
                        let countryName = 'India';
                        for (const tempCat of categories) {
                            const tempCountryField = tempCat.Fields?.find(field => {
                                const lower = field.FieldName?.toLowerCase();
                                return lower === 'country' || lower === 'countryid' || lower?.includes('country');
                            });
                            if (tempCountryField?.FieldValue) {
                                countryName = tempCountryField.FieldValue;
                                break;
                            }
                        }
                        processedValue = getNormalizedPhoneNumber(processedValue, countryName);
                    }

                    if (fieldInfo && fieldInfo.field.HtmlInputType === 'checkbox') {
                        processedValue = Array.isArray(f.FieldValue) ? f.FieldValue : (f.FieldValue ? f.FieldValue.split(',').map(s => s.trim()) : []);
                    }

                    patchedValues[f.FieldName] = processedValue;

                    if (fieldInfo) {
                        const isMandatory = fieldInfo.field.IsMendatory === true;

                        // If field exists but is NOT mandatory, we need to add it as custom
                        if (!isMandatory) {
                            const fieldToAdd = {
                                ...fieldInfo.field,
                                Id: fieldInfo.field.Id, // MUST use the official integer MasterFieldId, not a Lead schema UUID
                                FieldName: f.FieldName,
                                FieldValue: processedValue,
                                IsMendatory: fieldInfo.field.IsMendatory, // Keep original mandatory status
                                isCustom: true
                            };

                            customFieldsToAdd.push({
                                categoryId: fieldInfo.category.Id,
                                categoryName: fieldInfo.category.CategoryName,
                                field: fieldToAdd
                            });
                        }
                    } else if (!isApplicationForm) {
                        // Field is completely missing from formFields (e.g. no category or deleted), 
                        // but it has data. We MUST add it so the user can edit it (only for standard leads).
                        if (formFields.length > 0) {
                            const fallbackCategory = formFields[0];
                            const fieldToAdd = {
                                Id: f.MasterFieldId || `custom-${Math.random()}`,
                                FieldName: f.FieldName,
                                FieldLabel: f.FieldLabel || f.FieldName,
                                FieldValue: processedValue,
                                HtmlInputType: "text", // Default
                                IsMendatory: false,
                                isCustom: true
                            };
                            customFieldsToAdd.push({
                                categoryId: fallbackCategory.Id,
                                categoryName: fallbackCategory.CategoryName,
                                field: fieldToAdd
                            });
                        }
                    }
                }
            });
        });

        // Set form values - only patch fields that user hasn't edited yet
        setFormValues(prev => {
            const newValues = { ...prev };
            Object.keys(patchedValues).forEach(fieldName => {
                // Only patch if the user hasn't edited this field yet
                if (!userEditedFields.has(fieldName)) {
                    newValues[fieldName] = patchedValues[fieldName];
                }
            });
            return newValues;
        });

        // Instead of creating separate custom fields, merge them into formFields
        if (customFieldsToAdd.length > 0) {
            setFormFields(prevFormFields => {
                const updatedFormFields = prevFormFields.map(category => {
                    const customFieldsForThisCategory = customFieldsToAdd.filter(
                        item => item.categoryId === category.Id
                    );

                    if (customFieldsForThisCategory.length > 0) {
                        const existingCustomFields = category.MasterFields?.filter(f => f.isCustom) || [];
                        const newCustomFields = customFieldsForThisCategory.map(item => item.field);

                        // Filter out duplicates
                        const uniqueCustomFields = [...existingCustomFields, ...newCustomFields].filter((field, index, self) =>
                            index === self.findIndex(f => f.FieldName === field.FieldName)
                        );
                        
                        const uniqueCustomFieldNames = uniqueCustomFields.map(f => f.FieldName?.toLowerCase());

                        return {
                            ...category,
                            MasterFields: [
                                // Filter out original non-mandatory fields that have been marked as custom to prevent duplication
                                ...(category.MasterFields?.filter(f => !f.isCustom && !uniqueCustomFieldNames.includes(f.FieldName?.toLowerCase())) || []),
                                ...uniqueCustomFields
                            ]
                        };
                    }

                    return category;
                });

                return updatedFormFields;
            });
        }
    };


    useEffect(() => {
        if (formFields.length > 0) {
            setMandatoryFieldsByCategory(getVisibleFields());
        }
    }, [formFields]);

    const fetchFormFields = useCallback(async () => {
        setLoadingFields(true);
        try {
            // Fetch colleges and streams for dropdown binding (For both Custom Forms and Standard Forms)
            let colleges = [];
            let streams = [];
            let sessions = [];
            try {
                const [collegesRes, streamsRes, sessionsRes] = await Promise.all([
                    contactService.getColleges(),
                    contactService.getStreams(),
                    contactService.getSessions()
                ]);
                
                if (collegesRes?.statusCode === 200 || collegesRes?.status === 'Success') {
                    const rawData = collegesRes.data;
                    colleges = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || []);
                } else if (Array.isArray(collegesRes)) {
                    colleges = collegesRes;
                } else if (collegesRes?.data) {
                    colleges = typeof collegesRes.data === 'string' ? JSON.parse(collegesRes.data) : collegesRes.data;
                }
                
                if (streamsRes?.statusCode === 200 || streamsRes?.status === 'Success') {
                    const rawData = streamsRes.data;
                    streams = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || []);
                } else if (Array.isArray(streamsRes)) {
                    streams = streamsRes;
                } else if (streamsRes?.data) {
                    streams = typeof streamsRes.data === 'string' ? JSON.parse(streamsRes.data) : streamsRes.data;
                }

                if (sessionsRes?.statusCode === 200 || sessionsRes?.status === 'Success') {
                    const rawData = sessionsRes.data;
                    sessions = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || []);
                } else if (Array.isArray(sessionsRes)) {
                    sessions = sessionsRes;
                } else if (sessionsRes?.data) {
                    sessions = typeof sessionsRes.data === 'string' ? JSON.parse(sessionsRes.data) : sessionsRes.data;
                }
            } catch (err) {
                console.error("Error fetching options", err);
            }

            if (selectedForm && selectedForm.FormId) {
                // Fetch specific form fields
                const result = await getFormById(selectedForm.FormId, leadId);
                if (result.success && result.data) {
                    const formData = result.data;
                    let steps = [];

                    // Parse steps if needed or use directly
                    if (formData.Steps) {
                        steps = formData.Steps;
                    }

                    // Map steps to categories
                    const mappedSteps = steps.map(step => {
                        const stepFields = [];
                        if (step.Fields) {
                            step.Fields.forEach(field => {
                                let options = getFieldOptions(field);

                                const lowerLabel = (field.FieldLabel || '').toLowerCase();
                                if (field.FieldConstant === 'college' || field.FieldName === 'college_name' || field.FieldName === 'College' || lowerLabel.includes('college')) {
                                    options = Array.isArray(colleges) ? colleges.map(c => ({ OptionLabel: c.OptionLabel || c.CollegeName, OptionValue: c.OptionLabel || c.CollegeName })) : [];
                                } else if (field.FieldConstant === 'stream' || field.FieldName === 'stream_name' || field.FieldName === 'Stream' || lowerLabel.includes('stream')) {
                                    options = Array.isArray(streams) ? streams.map(s => ({ OptionLabel: s.StreamName, OptionValue: s.StreamName })) : [];
                                } else if (field.FieldConstant === 'session' || (field.FieldName || '').toLowerCase() === 'session_name' || (field.FieldName || '').toLowerCase() === 'session' || lowerLabel.includes('session')) {
                                    options = Array.isArray(sessions) && sessions.length > 0 ? sessions.map(s => ({ OptionLabel: s.OptionLabel || s.SessionName, OptionValue: s.OptionValue || s.SessionName })) : options;
                                }

                                stepFields.push({
                                    Id: field.FieldId || field.Id,
                                    FieldName: field.FieldName,
                                    FieldLabel: field.FieldLabel,
                                    IsMendatory: field.IsRequired,
                                    HtmlInputType: getHtmlInputType(field.FieldTypeId),
                                    MasterFormFieldOptions: options,
                                    FieldOrder: field.FieldOrder,
                                    Description: field.Placeholder,
                                    MasterFieldId: field.MasterFieldId,
                                    FieldValue: field.FieldValue,
                                    DefaultValue: field.DefaultValue
                                });
                            });
                            // Sort fields
                            stepFields.sort((a, b) => (a.FieldOrder || 0) - (b.FieldOrder || 0));
                        }
                        return {
                            Id: step.Id || `step-${Math.random()}`,
                            CategoryName: step.StepTitle || formData.Title || "Lead Details",
                            MasterFields: stepFields
                        };
                    });

                    setFormFields(mappedSteps);
                    initializeFormValues(mappedSteps);

                    if (mappedSteps.length > 1) {
                        setIsMultiStep(true);
                        setTotalSteps(mappedSteps.length);
                        setCurrentStep(0);
                    } else {
                        setIsMultiStep(false);
                    }
                }
            } else {
                // Default behavior: Fetch Master Fields
                const result = await getMasterFormFields();
                if (result.success) {

                    // Sort fields by FieldOrder within each category
                    const sortedData = result.data.map(category => ({
                        ...category,
                        MasterFields: category.MasterFields?.map(field => {
                            if (field.FieldConstant === 'college' || field.FieldName === 'college_name' || field.FieldName === 'College') {
                                return {
                                    ...field,
                                    HtmlInputType: 'select',
                                    MasterFormFieldOptions: Array.isArray(colleges) ? colleges.map(c => ({ OptionLabel: c.OptionLabel || c.CollegeName, OptionValue: c.OptionLabel || c.CollegeName })) : []
                                };
                            }
                            if (field.FieldConstant === 'stream' || field.FieldName === 'stream_name' || field.FieldName === 'Stream') {
                                return {
                                    ...field,
                                    HtmlInputType: 'select',
                                    MasterFormFieldOptions: Array.isArray(streams) ? streams.map(s => ({ OptionLabel: s.StreamName, OptionValue: s.StreamName })) : []
                                };
                            }
                            if (field.FieldConstant === 'session' || (field.FieldName || '').toLowerCase() === 'session' || (field.FieldName || '').toLowerCase() === 'session_name' || (field.FieldLabel || '').toLowerCase().includes('session')) {
                                return {
                                    ...field,
                                    HtmlInputType: 'select',
                                    MasterFormFieldOptions: Array.isArray(sessions) && sessions.length > 0 ? sessions.map(s => ({ OptionLabel: s.OptionLabel || s.SessionName, OptionValue: s.OptionValue || s.SessionName })) : (field.MasterFormFieldOptions || [])
                                };
                            }
                            return field;
                        }).sort((a, b) => {
                            // Handle null FieldOrder - push to end
                            if (a.FieldOrder === null && b.FieldOrder === null) return 0;
                            if (a.FieldOrder === null) return 1;
                            if (b.FieldOrder === null) return -1;
                            // Sort by FieldOrder ascending
                            return a.FieldOrder - b.FieldOrder;
                        })
                    }));
                    setFormFields(sortedData);
                    setIsMultiStep(false);

                    if (!isEditMode) {
                        initializeFormValues(sortedData);
                    }
                } else {
                    console.error("Failed to load form fields", result.message);
                }
            }
        } catch (error) {
            console.error("Error fetching form fields", error);
        } finally {
            setLoadingFields(false);
        }
    }, [isEditMode, selectedForm, leadId, isApplicationForm]);

    // Helper to map FieldTypeId to HTML Input Type (Corrected mapping)
    const getHtmlInputType = (typeId) => {
        switch (typeId) {
            case 1: return "text";
            case 2: return "textarea";
            case 3: return "number";
            case 4: return "date";
            case 5: return "checkbox";
            case 6: return "radio";
            case 7: return "select";
            case 8: return "email";
            case 9: return "password";
            case 10: return "phone";
            default: return "text";
        }
    };


    // Update the useEffect to call patchIncomingValues after formFields is set
    useEffect(() => {
        if (isOpen) {
            // Reset user edited fields when modal opens
            setUserEditedFields(new Set());
            fetchFormFields();
        }
    }, [isOpen, fetchFormFields]);

    // Add a separate useEffect to patch values when formFields are loaded
    // Use a ref to track if we've already patched to avoid infinite loops
    useEffect(() => {
        if (formFields.length > 0 && categories && categories.length > 0 && isEditMode && !hasPatchedRef.current) {
            patchIncomingValues(categories);
            hasPatchedRef.current = true;
        }
    }, [formFields]); // Only depend on formFields

    // Fetch Stages and Users for Assignment
    useEffect(() => {
        const fetchAssignmentData = async () => {
            setLoadingAssignment(true);
            try {
                const [stagesResult, usersResult] = await Promise.all([
                    getStageAndSubStages(),
                    getUsersForAssignLeads()
                ]);

                if (Array.isArray(stagesResult)) {
                    setStages(stagesResult);
                }
                
                if (usersResult.success && Array.isArray(usersResult.data)) {
                    setUsers(usersResult.data);
                }
            } catch (error) {
                console.error("Error fetching assignment data:", error);
            } finally {
                setLoadingAssignment(false);
            }
        };

        if (isOpen) {
            fetchAssignmentData();
        }
    }, [isOpen]);

    // Update sub-stages when stage changes
    useEffect(() => {
        if (selectedStage) {
            const stage = stages.find(s => s.stageId === selectedStage);
            const currentSubStages = stage?.subStages || stage?.SubStages || [];
            setSubStages(currentSubStages);
            
            // Auto-select first sub-stage if current one is not valid for this stage
            if (!currentSubStages.some(ss => ss.subStageId === selectedSubStage)) {
                const firstSub = currentSubStages[0];
                setSelectedSubStage(firstSub ? firstSub.subStageId : "");
            }
        } else {
            setSubStages([]);
            setSelectedSubStage("");
        }
    }, [selectedStage, stages]);

    // Reset the patch flag when modal closes
    useEffect(() => {
        if (!isOpen) {
            hasPatchedRef.current = false;
        }
    }, [isOpen]);
    const initializeFormValues = (data) => {
        const initialValues = {};
        data.forEach((category) => {
            category.MasterFields?.forEach((field) => {
                const fieldNameLower = (field.FieldName || "").toLowerCase();
                const isPhoneField = field.HtmlInputType === 'phone' || fieldNameLower.includes('phone') || fieldNameLower.includes('mobile');

                if (isPhoneField && field.FieldValue && typeof field.FieldValue === 'string' && !field.FieldValue.startsWith('+')) {
                    // Try to find country in the data
                    let countryName = 'India';
                    for (const cat of data) {
                        const countryField = cat.MasterFields?.find(f => {
                            const lower = f.FieldName?.toLowerCase();
                            return lower === 'country' || lower === 'countryid' || lower?.includes('country');
                        });
                        if (countryField?.FieldValue) {
                            countryName = countryField.FieldValue;
                            break;
                        }
                    }
                    initialValues[field.FieldName] = getNormalizedPhoneNumber(field.FieldValue, countryName);
                } else if (fieldNameLower === 'country' || fieldNameLower === 'countryid') {
                    const options = getFieldOptions(field);
                    const indiaOption = options.find(opt => {
                        const label = (opt.OptionLabel || opt.optionLabel || "").toLowerCase();
                        const val = (opt.OptionValue || opt.optionValue || "").toLowerCase();
                        return label === "india" || val === "in" || val === "india";
                    });

                    if (indiaOption) {
                        initialValues[field.FieldName] = indiaOption.OptionValue || indiaOption.optionValue;
                    } else {
                        // If no specific option found, we can try to set a fallback "IN" if it's likely a code-based list
                        // but usually it's better to stick to what the user requested: preselect India.
                        initialValues[field.FieldName] = "India"; // Default string fallback
                    }
                } else {
                    initialValues[field.FieldName] = field.FieldValue || field.DefaultValue || "";
                }
            });
        });
        setFormValues(initialValues);
    };

    const getVisibleFields = () => {
        const visibleFields = [];
        const isStandardLead = !selectedForm || !selectedForm.FormId;

        formFields.forEach((category) => {
            const categoryFields = category.MasterFields || [];

            // Filter fields
            const filteredFields = categoryFields.filter(field => {
                // If it's a "Standard Lead", ONLY show mandatory fields OR custom fields (which are manually added)
                if (isStandardLead) {
                    return field.IsMendatory || field.isCustom;
                }
                return true;
            });

            if (filteredFields.length > 0) {
                visibleFields.push({
                    categoryName: category.CategoryName,
                    categoryId: category.Id,
                    fields: filteredFields,
                    isCustom: false
                });
            }
        });
        return visibleFields;
    };

    // Improved validate single field function
    const validateField = async (fieldName, value) => {
        // Find if this field is mandatory in our form fields
        let isMandatoryField = false;
        for (const cat of formFields) {
            const f = cat.MasterFields?.find(field => field.FieldName === fieldName);
            if (f?.IsMendatory) {
                isMandatoryField = true;
                break;
            }
        }

        try {
            const schemaKey = getSchemaKey(fieldName);

            if (schemaKey && formValidationSchema.fields[schemaKey]) {
                // Validate via Yup schema if defined
                await formValidationSchema.fields[schemaKey].validate(value);
            } else if (isMandatoryField && (!value || value.toString().trim() === "")) {
                // Manually trigger required error if it is mandatory and empty
                throw new Error(`${fieldName} is required`);
            }

            // Clear error if validation passes (or if it's a non-schema field that is now filled)
            setFormErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        } catch (error) {
            // Set error if validation fails
            // Use custom label for mandatory fields if available
            let errorMsg = error.message;
            if (errorMsg.includes('is required') || errorMsg === `${fieldName} is required`) {
                // Find custom label
                let customLabel = fieldName;
                for (const cat of formFields) {
                    const f = cat.MasterFields?.find(field => field.FieldName === fieldName);
                    if (f?.FieldLabel) {
                        customLabel = f.FieldLabel;
                        break;
                    }
                }
                errorMsg = `${customLabel} is required`;
            }
            
            setFormErrors((prev) => ({
                ...prev,
                [fieldName]: errorMsg
            }));
        }
    };

    const handleFieldChange = (fieldName, value) => {
        setFormValues((prev) => ({
            ...prev,
            [fieldName]: value,
        }));

        // Track that this field has been edited by the user
        setUserEditedFields(prev => new Set([...prev, fieldName]));

        // Debounced real-time validation
        validateField(fieldName, value);
    };

    const handleDeleteField = (fieldId) => {
        let updatedFormFields = [...formFields];
        let fieldFound = false;

        // Remove from main formFields
        updatedFormFields = updatedFormFields.map((category) => {
            const filteredFields = category.MasterFields?.filter(
                (f) => f.Id !== fieldId
            ) || [];
            if (filteredFields.length !== category.MasterFields?.length) {
                fieldFound = true;
            }
            return {
                ...category,
                MasterFields: filteredFields,
            };
        });

        // Remove from customFields
        let updatedCustomFields = customFields.map((category) => {
            const filteredFields = category.fields?.filter(
                (f) => f.Id !== fieldId
            ) || [];
            return {
                ...category,
                fields: filteredFields,
            };
        }).filter(category => category.fields.length > 0);

        if (!fieldFound) {
            updatedCustomFields = updatedCustomFields.filter(
                (cat) => cat.fields.length > 0
            );
        }

        setFormFields(updatedFormFields);
        setCustomFields(updatedCustomFields);

        // Remove from formValues and errors
        setFormValues((prev) => {
            const newValues = { ...prev };
            Object.keys(newValues).forEach((key) => {
                const allFields = [
                    ...updatedFormFields.flatMap((c) => c.MasterFields || []),
                    ...updatedCustomFields.flatMap((c) => c.fields || []),
                ];
                const stillExists = allFields.some((f) => f.FieldName === key);
                if (!stillExists) delete newValues[key];
            });
            return newValues;
        });

        setFormErrors((prev) => {
            const newErrors = { ...prev };
            const allFields = [
                ...updatedFormFields.flatMap((c) => c.MasterFields || []),
                ...updatedCustomFields.flatMap((c) => c.fields || []),
            ];
            Object.keys(newErrors).forEach((key) => {
                const stillExists = allFields.some((f) => f.FieldName === key);
                if (!stillExists) delete newErrors[key];
            });
            return newErrors;
        });
    };

    const handleSelectCustomField = (field) => {

        const fieldExistsInMandatory = formFields.some(category =>
            category.MasterFields?.some(f => f.FieldName === field.FieldName && f.IsMendatory === true)
        );

        const fieldExistsInCustom = customFields.some(cat =>
            cat.fields?.some(f => f.FieldName === field.FieldName)
        );

        if (fieldExistsInMandatory || fieldExistsInCustom) {
            return;
        }

        const newField = {
            ...field,
            IsMendatory: field.IsMendatory, // Keep original mandatory status
            Id: field.Id || `custom-${Date.now()}`,
            isCustom: true,
        };

        const categoryExistsInMandatory = formFields.some(
            (cat) => cat.Id === field.CategoryId
        );

        if (categoryExistsInMandatory) {
            setFormFields((prev) =>
                prev.map((cat) => {
                    if (cat.Id === field.CategoryId) {
                        return {
                            ...cat,
                            MasterFields: [...(cat.MasterFields || []), newField],
                        };
                    }
                    return cat;
                })
            );
        } else {
            setCustomFields((prev) => {
                const existingCategoryIndex = prev.findIndex(
                    (cat) => cat.categoryId === field.CategoryId
                );

                if (existingCategoryIndex !== -1) {
                    const updated = [...prev];
                    updated[existingCategoryIndex] = {
                        ...updated[existingCategoryIndex],
                        fields: [...updated[existingCategoryIndex].fields, newField],
                    };
                    return updated;
                } else {
                    return [
                        ...prev.filter(cat => cat.categoryId !== field.CategoryId),
                        {
                            categoryName: field.CategoryName,
                            categoryId: field.CategoryId,
                            fields: [newField],
                            isCustom: true,
                        },
                    ];
                }
            });
        }

        setFormValues((prev) => ({
            ...prev,
            [field.FieldName]: field.DefaultValue || "",
        }));

    };

    // Handler for FieldSelectionModal - handles master field selection from form builder modal
    const handleSelectMasterField = (fieldData) => {
        // Convert from FieldSelectionModal format (camelCase) to QuickLeadModal format (PascalCase)
        const field = {
            Id: fieldData.masterFieldId || fieldData.Id || `master-${Date.now()}`,
            FieldName: fieldData.fieldName || fieldData.FieldName,
            FieldLabel: fieldData.fieldLabel || fieldData.FieldLabel,
            HtmlInputType: fieldData.htmlInputType || fieldData.HtmlInputType || 'text',
            IsMendatory: fieldData.isMendatory || fieldData.IsMendatory || false, // Keep original mandatory status
            isCustom: true,
            MasterFieldId: fieldData.masterFieldId || fieldData.Id,
            MasterFormFieldOptions: fieldData.options || fieldData.MasterFormFieldOptions || [],
            DefaultValue: fieldData.defaultValue || fieldData.DefaultValue || '',
            Placeholder: fieldData.placeholder || fieldData.Placeholder || '',
            CategoryId: fieldData.CategoryId,
            CategoryName: fieldData.CategoryName,
        };

        // Get the visible fields (what's actually being rendered in the form)
        const visibleCategories = getVisibleFields();

        const fieldAlreadyVisible = visibleCategories.some(category =>
            category.fields?.some(f =>
                f.FieldName === field.FieldName ||
                (f.Id && f.Id === field.Id) ||
                (f.MasterFieldId && f.MasterFieldId === field.MasterFieldId)
            )
        );

        // Also check in customFields
        const fieldExistsInCustom = customFields.some(cat =>
            cat.fields?.some(f =>
                f.FieldName === field.FieldName
            )
        );

        if (fieldAlreadyVisible || fieldExistsInCustom) {
            toast.info("This field is already added to the form");
            return;
        }

        let addedToExistingCategory = false;

        setFormFields((prev) => {
            const updated = prev.map((cat) => {
                // Match by category ID or category name
                if (cat.Id === field.CategoryId || cat.CategoryName === field.CategoryName) {
                    addedToExistingCategory = true;
                    return {
                        ...cat,
                        MasterFields: [...(cat.MasterFields || []), field],
                    };
                }
                return cat;
            });

            // If no matching category found, add to first category
            if (!addedToExistingCategory && updated.length > 0) {
                updated[0] = {
                    ...updated[0],
                    MasterFields: [...(updated[0].MasterFields || []), field],
                };
            }

            return updated;
        });

        // Initialize form value for this field
        setFormValues((prev) => ({
            ...prev,
            [field.FieldName]: field.DefaultValue || "",
        }));

        toast.success(`"${field.FieldLabel}" field added`);
    };

    // Fetch all master fields from backend for the FieldSelectionModal
    const fetchAllMasterFields = async () => {
        setMasterFieldsLoading(true);
        setMasterFieldsError(null);

        try {
            const response = await axios.get('/api/FormDesk/GetMasterFields');

            if (response.data?.status === "Success" && response.data?.data) {
                const parsedData = JSON.parse(response.data.data);
                setAllMasterFields(parsedData);
            } else {
                setMasterFieldsError(response.data?.message || 'Failed to fetch master fields');
            }
        } catch (error) {
            console.error('Error fetching master fields:', error);
            setMasterFieldsError('Failed to load master fields');
        } finally {
            setMasterFieldsLoading(false);
        }
    };

    // Fetch field types from backend for CreateMasterFieldModal
    const fetchFieldTypes = async () => {
        try {
            const result = await getFieldTypes();
            if (result.success && result.data) {
                setFieldTypes(result.data);
            }
        } catch (error) {
            console.error('Error fetching field types:', error);
        }
    };

    const handleAddField = () => {
        // Fetch all master fields and field types when opening the modal
        fetchAllMasterFields();
        fetchFieldTypes();
        setIsAddFieldModalOpen(true);
    };

    // Render Field - Now with error handling
    const renderField = useCallback((field, fieldIdx, categoryIdx) => {
        const fieldType =
            field.HtmlInputType || field.TypeName?.toLowerCase() || "text";
        const fieldValue = formValues[field.FieldName] || (fieldType === 'checkbox' ? [] : "");
        const fieldError = formErrors[field.FieldName];
        const fieldId = field.Id || `${categoryIdx}-${fieldIdx}`;
        const isHovered = hoveredFieldId === fieldId;
        // Check if this field should be disabled
        const isFieldDisabled = isEditMode && disabledFields.has(field.FieldName);

        const commonProps = {
            id: fieldId,
            name: field.FieldName,
            value: fieldValue,
            placeholder: field.Placeholder || field.FieldLabel,
            required: field.IsMendatory,
            disabled: isFieldDisabled, // Add this line
            onChange: (e) => {
                let val = e.target.value;
                if (field.FieldName.toLowerCase() === 'semester') {
                    val = val.replace(/\D/g, ''); // Allow only digits
                }
                handleFieldChange(field.FieldName, val);
            },
            onBlur: (e) => validateField(field.FieldName, e.target.value),
            className: clsx(
                "transition-all duration-200",
                fieldError && "border-red-300 focus:border-red-500 focus:ring-red-500",
                isFieldDisabled && "bg-gray-100 dark:bg-dark-600 cursor-not-allowed opacity-75" // Add styling for disabled fields
            ),
        };

        const containerProps = {
            fieldId,
            field,
            isHovered,
            onMouseEnter: () => setHoveredFieldId(fieldId),
            onMouseLeave: () => setHoveredFieldId(null),
            onDelete: handleDeleteField,
            fieldRefs,
            error: fieldError,
            hideDescription: isApplicationForm
        };

        switch (fieldType) {
            case "textarea":
                return (
                    <FieldContainer {...containerProps}>
                        <Textarea {...commonProps} rows={4} />
                    </FieldContainer>
                );

            case "select":
            case "dropdown": {
                const options = getFieldOptions(field);
                return (
                    <FieldContainer {...containerProps}>
                        <Select
                            {...commonProps}
                            onChange={(e) => {
                                // Handle both event objects and direct values, ensure string value
                                const selectedValue = e?.target?.value ?? e;
                                handleFieldChange(field.FieldName, selectedValue);
                            }}
                        >
                            <option value="">Select {field.FieldLabel}</option>
                            {options.map((opt, idx) => (
                                <option key={idx} value={opt.OptionValue || opt.optionValue}>
                                    {opt.OptionLabel || opt.optionLabel}
                                </option>
                            ))}
                        </Select>
                    </FieldContainer>
                );
            }

            case "checkbox":
                {
                    const checkOptions = getFieldOptions(field);
                    // If no options, basic bool checkbox (fallback)
                    if (checkOptions.length === 0) {
                        return (
                            <FieldContainer {...containerProps} isCheckbox>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        {...commonProps}
                                        checked={fieldValue === true || fieldValue === "true"}
                                        onChange={(e) =>
                                            handleFieldChange(field.FieldName, e.target.checked)
                                        }
                                        className="h-4 w-4 focus:ring-2 border-gray-300 rounded text-primary-600 focus:ring-primary-500"
                                    />
                                    <label
                                        htmlFor={fieldId}
                                        className="ml-2 block text-sm text-gray-900 dark:text-gray-100"
                                    >
                                        {field.FieldLabel}
                                    </label>
                                </div>
                            </FieldContainer>
                        );
                    }
                    // Multi-option checkbox
                    return (
                        <FieldContainer {...containerProps}>
                            <div className="space-y-2 p-1">
                                {checkOptions.map((option, idx) => {
                                    const optionValue = option.OptionValue || option.optionValue;
                                    const optionLabel = option.OptionLabel || option.optionLabel;
                                    const isChecked = Array.isArray(fieldValue) ? fieldValue.includes(optionValue) : false;

                                    return (
                                        <label key={idx} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600 p-2 rounded-md transition-colors duration-150">
                                            <div className="relative mr-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        let newValues = Array.isArray(fieldValue) ? [...fieldValue] : [];
                                                        if (e.target.checked) {
                                                            newValues.push(optionValue);
                                                        } else {
                                                            newValues = newValues.filter(v => v !== optionValue);
                                                        }
                                                        handleFieldChange(field.FieldName, newValues);
                                                    }}
                                                    className="sr-only"
                                                />
                                                <div className={clsx(
                                                    "w-5 h-5 rounded border-2 transition-all duration-200 cursor-pointer flex items-center justify-center",
                                                    isChecked
                                                        ? "border-primary-600 bg-primary-600"
                                                        : "bg-white dark:bg-dark-700 border-gray-300 dark:border-gray-500 hover:border-primary-400"
                                                )}>
                                                    {isChecked && (
                                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-sm font-medium select-none text-gray-700 dark:text-gray-200">
                                                {optionLabel}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </FieldContainer>
                    );
                }

            case "radio": {
                const radioOptions = getFieldOptions(field);
                return (
                    <FieldContainer {...containerProps}>
                        <div className="space-y-2 p-1">
                            {radioOptions.map((option, idx) => {
                                const optionId = `${fieldId}-${idx}`;
                                const optionValue = option.OptionValue || option.optionValue;
                                const optionLabel = option.OptionLabel || option.optionLabel;
                                const isSelected = fieldValue === optionValue;

                                return (
                                    <label key={idx} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600 p-2 rounded-md transition-colors duration-150">
                                        <div className="relative mr-3">
                                            <input
                                                type="radio"
                                                id={optionId}
                                                name={field.FieldName}
                                                value={optionValue}
                                                checked={isSelected}
                                                onChange={(e) =>
                                                    handleFieldChange(field.FieldName, e.target.value)
                                                }
                                                className="sr-only"
                                            />
                                            <div className={clsx(
                                                "w-5 h-5 rounded-full border-2 transition-all duration-200 cursor-pointer flex items-center justify-center",
                                                isSelected
                                                    ? "border-primary-600 bg-primary-600"
                                                    : "bg-white dark:bg-dark-700 border-gray-300 dark:border-gray-500 hover:border-primary-400"
                                            )}>
                                                {isSelected && (
                                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                                )}
                                            </div>
                                        </div>
                                        <span className="text-sm font-medium select-none text-gray-700 dark:text-gray-200">
                                            {optionLabel}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </FieldContainer>
                );
            }

            // case "number":
            //     return (
            //         <FieldContainer {...containerProps}>
            //             <Input {...commonProps} type="number" />
            //         </FieldContainer>
            //     );

            case "email":
                return (
                    <FieldContainer {...containerProps}>
                        <Input {...commonProps} type="email" />
                    </FieldContainer>
                );

            case "date":
                return (
                    <FieldContainer {...containerProps}>
                        <Input {...commonProps} type="date" />
                    </FieldContainer>
                );

            case "tel":
            case "phone":
                return (
                    <FieldContainer {...containerProps}>
                        <div className={clsx(
                            "relative rounded-md border transition-all duration-200 bg-white dark:bg-dark-700",
                            fieldError
                                ? "border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500"
                                : "border-gray-300 dark:border-dark-500 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500"
                        )}>
                            {(() => {
                                // Derive country code from the Country field in formValues
                                const countryValue = formValues['country'] || formValues['Country'] || formValues['countryid'] || formValues['CountryId'] || 'India';
                                const countryCode = countryNameToCode[String(countryValue).toLowerCase()] || 'IN';

                                return (
                                    <PhoneInput
                                        international
                                        country={countryCode}
                                        defaultCountry="IN"
                                        value={fieldValue}
                                        onChange={(value) => handleFieldChange(field.FieldName, value)}
                                        onCountryChange={(countryCode) => {
                                            if (countryCode && enLocale[countryCode]) {
                                                const countryName = enLocale[countryCode];
                                                let matchedFieldName = null;
                                                let matchedValue = null;

                                                // Lookup the "Country" field in the form schema
                                                // First check standard form fields
                                                for (const cat of formFields) {
                                                    const cField = cat.MasterFields?.find(f => {
                                                        const lower = f.FieldName?.toLowerCase();
                                                        return lower === 'country' || lower === 'countryid';
                                                    });
                                                    if (cField) {
                                                        matchedFieldName = cField.FieldName;
                                                        const opts = getFieldOptions(cField);
                                                        const opt = opts.find(o =>
                                                            (o.OptionLabel || o.optionLabel || '').toLowerCase() === countryName.toLowerCase()
                                                        );
                                                        matchedValue = opt ? (opt.OptionValue || opt.optionValue) : countryName;
                                                        break;
                                                    }
                                                }

                                                // If not found, check custom fields
                                                if (!matchedFieldName) {
                                                    for (const cat of customFields) {
                                                        const cField = cat.fields?.find(f => {
                                                            const lower = f.FieldName?.toLowerCase();
                                                            return lower === 'country' || lower === 'countryid';
                                                        });
                                                        if (cField) {
                                                            matchedFieldName = cField.FieldName;
                                                            const opts = getFieldOptions(cField);
                                                            const opt = opts.find(o =>
                                                                (o.OptionLabel || o.optionLabel || '').toLowerCase() === countryName.toLowerCase()
                                                            );
                                                            matchedValue = opt ? (opt.OptionValue || opt.optionValue) : countryName;
                                                            break;
                                                        }
                                                    }
                                                }

                                                if (matchedFieldName && matchedValue) {
                                                    handleFieldChange(matchedFieldName, matchedValue);
                                                }
                                            }
                                        }}
                                        placeholder={field.Placeholder || "Enter phone number"}
                                        disabled={isFieldDisabled}
                                        className={clsx(
                                            "p-2 bg-transparent",
                                            isFieldDisabled && "bg-gray-100 dark:bg-dark-600 cursor-not-allowed opacity-75"
                                        )}
                                        numberInputProps={{
                                            className: "w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none", // Remove default input styles
                                            required: field.IsMendatory
                                        }}
                                    />
                                );
                            })()}
                        </div>
                    </FieldContainer>
                );

            case "password":
                return (
                    <FieldContainer {...containerProps}>
                        <div className="relative">
                            <Input
                                {...commonProps}
                                type={passwordVisible[fieldId] ? "text" : "password"}
                                className={clsx(
                                    commonProps.className,
                                    "pr-10" // Make room for the toggle button
                                )}
                            />
                            <button
                                type="button"
                                onClick={() => setPasswordVisible(prev => ({
                                    ...prev,
                                    [fieldId]: !prev[fieldId]
                                }))}
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                                tabIndex={-1}
                            >
                                {passwordVisible[fieldId] ? (
                                    <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                                ) : (
                                    <EyeIcon className="h-5 w-5" aria-hidden="true" />
                                )}
                            </button>
                        </div>
                    </FieldContainer>
                );

            default:
                return (
                    <FieldContainer {...containerProps}>
                        <Input {...commonProps} type="text" />
                    </FieldContainer>
                );
        }
    }, [formValues, hoveredFieldId, fieldRefs, formErrors, isEditMode, disabledFields, passwordVisible, formFields, customFields]);

    const handleClose = () => {
        setFormValues({});
        setFormErrors({});
        setUserEditedFields(new Set());
        setHoveredFieldId(null);
        setDisabledFields(new Set());
        setPasswordVisible({});
        setIsPaymentModalOpen(false);

        // Reset Assignment Configuration
        setSelectedStage("");
        setSelectedSubStage("");
        setAssignmentType("rule");
        setSelectedOwner("");
        setIsAssignmentConfigOpen(false);

        onClose();
    };

    // Comprehensive validation on save
    const handleSave = async () => {
        setIsSaving(true);

        const valuesToValidate = {};
        const fieldNameMapping = {};

        try {
            // Map form values to validation schema keys for schema validation
            Object.keys(formValues).forEach(key => {
                const schemaKey = getSchemaKey(key);
                if (schemaKey) {
                    valuesToValidate[schemaKey] = formValues[key];
                    fieldNameMapping[schemaKey] = key;
                }
            });

            // Validate all fields using the schema to catch invalid formats (phone, email, etc.)
            await formValidationSchema.validate(valuesToValidate, { abortEarly: false });

            // Check for any remaining mandatory fields (applies to both standard and form-specific modes)
            const visibleCategories = getVisibleFields();

            visibleCategories.forEach(cat => {
                cat.fields.forEach(field => {
                    const lower = (field.FieldName || '').toLowerCase();
                    if (lower.includes('lastname') || lower.includes('last_name')) {
                        field.IsMendatory = false;
                    }
                });
            });
            let hasEmptyMandatory = false;
            const newErrors = {};

            visibleCategories.forEach((category) => {
                category.fields.forEach((field) => {
                    if (
                        field.IsMendatory &&
                        (!formValues[field.FieldName] || formValues[field.FieldName].toString().trim() === "")
                    ) {
                        hasEmptyMandatory = true;
                        newErrors[field.FieldName] = `${field.FieldLabel} is required`;
                    }
                });
            });

            const totalErrors = { ...formErrors, ...newErrors };
            if (Object.keys(totalErrors).length > 0) {
                setFormErrors(totalErrors);
                toast.error(hasEmptyMandatory ? "Please fill all mandatory fields correctly" : "Please fix the validation errors before submitting");

                // Auto-scroll to first error
                const firstErrorField = Object.keys(newErrors)[0];
                if (firstErrorField && fieldRefs[firstErrorField]) {
                    setTimeout(() => {
                        fieldRefs[firstErrorField].scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }, 100);
                }

                setIsSaving(false);
                return;
            }

            // Build fieldSubmissions array
            const fieldSubmissions = [];
            const seenFields = new Set(); // Track unique masterFieldId to avoid duplicates
            
            let collegeId = null;
            let streamId = null;
            let semester = null;

            [...formFields, ...customFields].forEach((category) => {
                const fields = category.MasterFields || category.fields || [];
                fields.forEach((field) => {
                    if (formValues[field.FieldName] !== undefined) {
                        // Use MasterFieldId if available (for form-specific fields), otherwise use Id
                        const masterFieldId = field.MasterFieldId || field.Id;
                        let fieldValue = formValues[field.FieldName];

                        const uniqueKey = masterFieldId;
                        if (!seenFields.has(uniqueKey)) {
                            seenFields.add(uniqueKey);

                            // Get the field type to determine value format
                            const fieldType = field.HtmlInputType || field.TypeName?.toLowerCase() || "text";

                            // Convert arrays to comma-separated strings (for checkboxes and multi-select)
                            // This matches the FormPage.jsx behavior
                            if (Array.isArray(fieldValue)) {
                                fieldValue = fieldValue.join(', ');
                            }

                            fieldSubmissions.push({
                                masterFieldId: masterFieldId,
                                fieldId: field.Id, // Numerical FieldId
                                masterFieldName: field.FieldName,
                                value: fieldValue,
                            });
                        }
                    }
                });
            });

            let result;

            if (isApplicationForm) {
                // Application submission mode - call SubmitApplicationAfterStageChange API
                const applicationSubmissionData = fieldSubmissions.map(fs => ({
                    FieldId: fs.fieldId, // Use numerical FieldId for application submission
                    Value: fs.value
                }));

                const payload = {
                    formId: selectedForm?.FormId || null,
                    leadId: leadId,
                    fieldJson: JSON.stringify(applicationSubmissionData)
                };

                result = await submitApplicationAfterStageChange(payload);

                if (result.success) {
                    toast.success(result.message || "Application submitted successfully!");
                    
                    if (isComingFromPayment) {
                        if (onLeadUpdated && typeof onLeadUpdated === 'function') {
                            onLeadUpdated();
                        }
                        handleClose();
                    } else {
                        setIsPaymentModalOpen(true);
                    }
                } else {
                    toast.error(result.message || "Failed to submit application");
                }
            } else if (isEditMode) {
                // Update mode - call updateLeadDetail API
                const payload = {
                    leadId: leadId,
                    companyId: getCompanyIdFromToken(),
                    loggedInUserId: getUserIdFromToken(),
                    fieldSubmissions: fieldSubmissions,
                    collegeId: collegeId,
                    streamId: streamId,
                    semester: semester ? parseInt(semester) : null
                };

                result = await updateLeadDetail(payload);

                if (result.success) {
                    toast.success(result.message || "Lead updated successfully!");

                    if (onLeadUpdated && typeof onLeadUpdated === 'function') {
                        onLeadUpdated();
                    }
                    handleClose();
                } else {
                    toast.error(result.message || "Failed to update lead");
                }
            // } else {
            //     // Create mode - call SubmitFormForLead API
            //     const payload = {
            //         fieldSubmissions,
            //         LeadSourceFormId: selectedForm?.FormId || null
            //     };

            //     result = await SubmitFormForLead(payload);

            //     if (result.success) {
            //         toast.success(result.message || "Lead created successfully!");

            //         // 1. Close the modal instantly for a snappy UI
            //         handleClose();
            //         // // For new lead, set the lead ID from response
            //         // const newLeadId = result.data?.data?.LeadId || result.data?.leadId || result.data?.data?.id || result.data?.id;
            //         // if (newLeadId) {
            //         //     setCurrentLeadId(newLeadId);
            //         // }

            //         // 2. Safely execute the background SWR refresh and parent updates
            //         try {
            //             const newLeadId = result.data?.data?.LeadId || result.data?.leadId || result.data?.data?.id || result.data?.id;
            //             if (newLeadId) {
            //                 setCurrentLeadId(newLeadId);
            //             }

            //         // --- ADD THIS BLOCK TO TRIGGER AN INSTANT BACKGROUND REFRESH ---
            //         mutate(
            //             key => Array.isArray(key) && key[0] === 'leads',
            //             undefined,
            //             { revalidate: true }
            //         );
                    
            //         if (onLeadUpdated && typeof onLeadUpdated === 'function') {
            //             onLeadUpdated();
            //         }catch(syncError){console.error}
                
            //     } else {
            //         toast.error(result.message || "Failed to save lead");
            //     }
            // }
            } else {
                // Create mode - call SubmitFormForLead API
                const payload = {
                    fieldSubmissions,
                    LeadSourceFormId: selectedForm?.FormId || null,
                    CollegeId: collegeId,
                    StreamId: streamId,
                    Semester: semester ? parseInt(semester) : null,
                    AssignmentSettings: {
                        stageId: selectedStage || null,
                        subStageId: selectedSubStage || null,
                        isOwnerSelected: assignmentType === 'owner',
                        ownerId: assignmentType === 'owner' ? selectedOwner : null
                    }
                };

                result = await SubmitFormForLead(payload);

                if (result.success) {
                    toast.success(result.message || "Lead created successfully!");

                    // 1. Close the modal instantly for a snappy UI
                    handleClose();

                    // 2. Safely execute the background SWR refresh and parent updates
                    try {
                        const newLeadId = result.data?.data?.LeadId || result.data?.leadId || result.data?.data?.id || result.data?.id;
                        if (newLeadId) {
                            setCurrentLeadId(newLeadId);
                        }
                        
                        mutate(
                            key => Array.isArray(key) && key[0] === 'leads',
                            undefined,
                            { revalidate: true }
                        );

                        if (onLeadUpdated && typeof onLeadUpdated === 'function') {
                            onLeadUpdated();
                        }
                    } catch (syncError) {
                        console.error("Background sync error:", syncError);
                    }
                    
                } else {
                    toast.error(result.message || "Failed to save lead");
                }
            }
        } catch (error) {
            if (error.name === 'ValidationError') {
                // Handle Yup validation errors
                const newErrors = {};
                error.inner.forEach((err) => {
                    // Map back to original field names
                    const originalFieldName = fieldNameMapping[err.path] || err.path;
                    newErrors[originalFieldName] = err.message;
                });
                setFormErrors(newErrors);
                toast.error("Please fix the validation errors before submitting");

                // Auto-scroll to first error
                const firstErrorField = Object.keys(newErrors)[0];
                if (firstErrorField && fieldRefs[firstErrorField]) {
                    setTimeout(() => {
                        fieldRefs[firstErrorField].scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }, 100);
                }
            } else {
                console.error("Error saving lead:", error);
                toast.error("An unexpected error occurred while saving the lead");
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <PaymentDetailsModal
                isOpen={isPaymentModalOpen}
                onClose={() => {
                    // console.log("QuickLeadModal: PaymentDetailsModal onClose triggered");
                    setIsPaymentModalOpen(false);
                    
                    // Now that all is done, refresh background data
                    if (onLeadUpdated && typeof onLeadUpdated === 'function') {
                        onLeadUpdated();
                    }
                    
                    // Trigger SWR revalidation
                    mutate(
                        key => Array.isArray(key) && key[0] === 'leads',
                        undefined,
                        { revalidate: true }
                    );
                    
                    handleClose(); // Close the QuickLeadModal completely after payment modal is closed
                }}
                leadId={currentLeadId}
            />
            <FieldSelectionModal
                isOpen={isAddFieldModalOpen}
                onClose={() => setIsAddFieldModalOpen(false)}
                masterFields={allMasterFields}
                masterFieldsLoading={masterFieldsLoading}
                masterFieldsError={masterFieldsError}
                onSelectField={handleSelectMasterField}
                onRefreshMasterFields={fetchAllMasterFields}
                fieldTypes={fieldTypes}
            />

            <Transition appear show={isOpen && !isPaymentModalOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="fixed inset-0 z-[99998] flex items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
                    onClose={() => { }}
                    initialFocus={cancelButtonRef}
                >
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="absolute inset-0 bg-gray-900/50 backdrop-blur dark:bg-black/30" />
                    </TransitionChild>

                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <DialogPanel className="relative flex w-full max-w-2xl origin-top flex-col overflow-hidden rounded-xl bg-white shadow-2xl transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
                            {/* HEADER */}
                            <div className="border-b border-gray-200 dark:border-dark-600 px-6 py-4 flex items-center justify-between bg-gray-50 dark:bg-dark-700">
                                <div className="flex-1">
                                    <h2 className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                                        {isApplicationForm ? "Application Form" : isEditMode ? "Edit Lead" : "Quick Lead"}
                                    </h2>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                        {isApplicationForm 
                                            ? "Complete the application by filling the required details below"
                                            : isEditMode
                                            ? "Update lead information by editing the fields below"
                                            : "Add a new lead quickly by filling mandatory fields"}
                                    </p>
                                </div>

                                <div className="flex items-center gap-3">
                                    {!isApplicationForm && (
                                        <>
                                            <div
                                                className="flex items-center gap-1.5 bg-white dark:bg-dark-750 rounded-lg px-3 py-1.5 border border-gray-200 dark:border-dark-600 shadow-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors duration-200"
                                                onClick={handleAddField}
                                            >
                                                <PlusIcon className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                                                <span className="text-xs font-medium text-primary-600 dark:text-primary-400">Add Field</span>
                                            </div>

                                            <div className="h-6 w-px bg-gray-300 dark:bg-dark-600"></div>
                                        </>
                                    )}

                                    <Button
                                        onClick={handleClose}
                                        variant="flat"
                                        isIcon
                                        className="size-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-600 dark:hover:text-gray-300 transition-all duration-200"
                                    >
                                        <XMarkIcon className="size-5" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex flex-col overflow-y-auto px-6 py-5 max-h-[70vh]">
                                {loadingFields ? (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                        <span className="ml-3 text-gray-600">Loading fields...</span>
                                    </div>
                                ) : mandatoryFieldsByCategory.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No fields found
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        {mandatoryFieldsByCategory.map((category, categoryIdx) => {
                                            if (isMultiStep && categoryIdx !== currentStep) {
                                                return null;
                                            }
                                            return (
                                                <div key={categoryIdx} className="space-y-4">
                                                    <h4
                                                        className={clsx(
                                                            "text-sm font-medium border-b pb-2 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-dark-600"
                                                        )}
                                                    >
                                                        {category.categoryName}
                                                    </h4>
                                                    <div className="space-y-3">
                                                        {category.fields.map((field, fieldIdx) => (
                                                            <div
                                                                key={field.Id || fieldIdx}
                                                                className="animate-fadeInUp"
                                                                style={{
                                                                    animationDelay: `${fieldIdx * 80}ms`,
                                                                    animationDuration: "400ms",
                                                                }}
                                                            >
                                                                {renderField(field, fieldIdx, categoryIdx)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )
                                        })}

                                        {/* Assignment Configuration Section */}
                                        {!isApplicationForm && !isEditMode && (!selectedForm || !selectedForm.FormId) && (
                                            <div 
                                                ref={assignmentSectionRef} 
                                                className={clsx(
                                                    "transition-all duration-500 ease-in-out overflow-hidden animate-fadeInUp",
                                                    isAssignmentConfigOpen 
                                                        ? "max-h-[1000px] opacity-100 mt-8 pt-6 border-t border-gray-200 dark:border-dark-600" 
                                                        : "max-h-0 opacity-0 mt-0 pt-0 border-t-0"
                                                )} 
                                                style={{ animationDelay: '200ms' }}
                                            >
                                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                                                    <ArrowRightIcon className="w-4 h-4 text-primary-600" />
                                                    Assignment Configuration
                                                </h4>
                                                
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        {/* Assign Stage */}
                                                        <div className="space-y-1.5">
                                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                                                Assign Stage
                                                            </label>
                                                            <Select
                                                                value={selectedStage}
                                                                onChange={(e) => setSelectedStage(e.target.value)}
                                                                className="w-full text-sm"
                                                            >
                                                                <option value="">Select Stage</option>
                                                                {stages.map((stage) => (
                                                                    <option key={stage.stageId} value={stage.stageId}>
                                                                        {stage.stageName}
                                                                    </option>
                                                                ))}
                                                            </Select>
                                                        </div>

                                                        {/* Assign Sub Stage */}
                                                        <div className="space-y-1.5">
                                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                                                Assign Sub Stage
                                                            </label>
                                                            <Select
                                                                value={selectedSubStage}
                                                                onChange={(e) => setSelectedSubStage(e.target.value)}
                                                                className="w-full text-sm"
                                                                disabled={!selectedStage}
                                                            >
                                                                <option value="">Select Sub Stage</option>
                                                                {subStages.map((subStage) => (
                                                                    <option key={subStage.subStageId} value={subStage.subStageId}>
                                                                        {subStage.subStageName}
                                                                    </option>
                                                                ))}
                                                            </Select>
                                                        </div>
                                                    </div>

                                                    <div className="mt-4">
                                                        {/* Assign Owner button + inline dropdown on same row */}
                                                        <div className="flex items-center gap-2" ref={ownerSelectRef}>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const nextType = assignmentType === "owner" ? "rule" : "owner";
                                                                    setAssignmentType(nextType);
                                                                    
                                                                    // Default to current user if switching to owner mode and none selected
                                                                    if (nextType === "owner" && !selectedOwner) {
                                                                        const loggedInUserId = getUserIdFromToken();
                                                                        if (loggedInUserId) {
                                                                            setSelectedOwner(String(loggedInUserId));
                                                                        }
                                                                    }
                                                                }}
                                                                className={clsx(
                                                                    "flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-all duration-200 whitespace-nowrap",
                                                                    assignmentType === "owner"
                                                                        ? "bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-600 dark:text-primary-300"
                                                                        : "bg-white border-gray-300 text-gray-600 hover:border-primary-400 hover:text-primary-600 dark:bg-dark-700 dark:border-dark-500 dark:text-gray-400 dark:hover:border-primary-500"
                                                                )}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                                </svg>
                                                                Assign Owner
                                                            </button>

                                                            {assignmentType === "owner" && (
                                                                <div className="flex-1 animate-fadeIn">
                                                                    <Select
                                                                        value={selectedOwner}
                                                                        onChange={(e) => setSelectedOwner(e.target.value)}
                                                                        className="w-full text-sm"
                                                                    >
                                                                        <option value="">Select Owner</option>
                                                                        {users.map((user) => (
                                                                            <option key={user.userId} value={user.userId}>
                                                                                {user.fullName}
                                                                            </option>
                                                                        ))}
                                                                    </Select>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                        )}
                                        {customFields.map((category, categoryIdx) => (
                                            <div key={`custom-${categoryIdx}`} className="space-y-4">
                                                <h4 className={clsx(
                                                    "text-sm font-medium border-b pb-2 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-dark-600"
                                                )}>
                                                    {category.categoryName} <span className="text-xs text-primary-600 ml-1"></span>
                                                </h4>
                                                <div className="space-y-3">
                                                    {category.fields.map((field, fieldIdx) => (
                                                        <div
                                                            key={field.Id || fieldIdx}
                                                            className="animate-fadeInUp"
                                                            style={{
                                                                animationDelay: `${fieldIdx * 80}ms`,
                                                                animationDuration: "400ms",
                                                            }}
                                                        >
                                                            {renderField(field, fieldIdx, categoryIdx)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* FOOTER */}
                            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between dark:border-dark-600">
                                <div className="flex-1">
                                    {!isApplicationForm && !isEditMode && (!selectedForm || !selectedForm.FormId) && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const nextState = !isAssignmentConfigOpen;
                                                setIsAssignmentConfigOpen(nextState);
                                                if (nextState) {
                                                    // Increased timeout to wait for transition to start gaining height
                                                    setTimeout(() => {
                                                        assignmentSectionRef.current?.scrollIntoView({ 
                                                            behavior: 'smooth', 
                                                            block: 'start' 
                                                        });
                                                    }, 300);
                                                }
                                            }}
                                            className={clsx(
                                                "text-xs font-bold px-4 py-2 rounded-full transition-all duration-300 flex items-center gap-2 group border-2 shadow-sm",
                                                isAssignmentConfigOpen 
                                                    ? "bg-primary-600 text-white border-primary-600 shadow-primary-200/50 hover:bg-primary-700 hover:border-primary-700" 
                                                    : "text-primary-600 bg-white border-primary-100 hover:border-primary-600 hover:shadow-md hover:-translate-y-0.5"
                                            )}
                                        >
                                            <AdjustmentsHorizontalIcon className={clsx(
                                                "w-4 h-4 transition-all duration-500",
                                                isAssignmentConfigOpen ? "rotate-180 scale-110" : "group-hover:rotate-45"
                                            )} />
                                            <span>Configure Assignment</span>
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <Button
                                        variant="default"
                                        onClick={handleClose}
                                        ref={cancelButtonRef}
                                        disabled={isSaving}
                                        className="transition-all duration-200 hover:scale-105"
                                    >
                                        Cancel
                                    </Button>
                                {isEditMode && !isApplicationForm ? (
                                    <Button
                                        variant="filled"
                                        onClick={handleSave}
                                        disabled={isSaving || loadingFields}
                                        className={clsx(
                                            "bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 text-white transition-all duration-200 hover:scale-105 hover:shadow-lg transform-gpu"
                                        )}
                                    >
                                        {isSaving ? "Updating..." : "Update"}
                                    </Button>
                                ) : (
                                    <>
                                        {isMultiStep && currentStep > 0 && (
                                            <Button
                                                variant="flat"
                                                onClick={() => setCurrentStep(prev => prev - 1)}
                                                className="mr-auto font-bold text-[#fe0046] hover:bg-red-50 hover:text-[#d0003a] transition-all"
                                                unstyled={true}
                                            >
                                                <span>Back</span>
                                                <ArrowRightIcon className="ml-1.5 h-4 w-4 stroke-[2.5px]" />
                                            </Button>
                                        )}
                                        {isMultiStep && currentStep < totalSteps - 1 ? (
                                            <Button
                                                variant="filled"
                                                onClick={async () => {
                                                    // Validate current step fields before moving next
                                                    const currentCategory = mandatoryFieldsByCategory[currentStep];
                                                    let hasError = false;
                                                    const nextErrors = { ...formErrors };

                                                    currentCategory.fields.forEach(field => {
                                                        if (field.IsMendatory && !formValues[field.FieldName]) {
                                                            hasError = true;
                                                            nextErrors[field.FieldName] = "This field is required";
                                                        }
                                                    });

                                                    if (hasError) {
                                                        setFormErrors(nextErrors);
                                                        toast.error("Please fill all mandatory fields");

                                                        // Auto-scroll to first error in current step
                                                        const firstErrorField = Object.keys(nextErrors).find(key =>
                                                            currentCategory.fields.some(f => f.FieldName === key)
                                                        );
                                                        if (firstErrorField && fieldRefs[firstErrorField]) {
                                                            setTimeout(() => {
                                                                fieldRefs[firstErrorField].scrollIntoView({
                                                                    behavior: 'smooth',
                                                                    block: 'center'
                                                                });
                                                            }, 100);
                                                        }
                                                        return;
                                                    }

                                                    setCurrentStep(prev => prev + 1);
                                                }}
                                                className="bg-primary-600 text-white"
                                            >
                                                Next
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="filled"
                                                onClick={handleSave}
                                                disabled={isSaving || loadingFields}
                                                className={clsx(
                                                    "bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 text-white transition-all duration-200 hover:scale-105 hover:shadow-lg transform-gpu"
                                                )}
                                            >
                                                {isSaving ? (
                                                    <>
                                                        <svg
                                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            />
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                            />
                                                        </svg>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    isApplicationForm ? "Save Application" : "Save Lead"
                                                )}
                                            </Button>
                                        )}
                                    </>
                                )}
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </Dialog>
            </Transition>
        </>
    );
};

export default QuickLeadModal;
